/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { mcpEndpointsUpdate } from "../funcs/mcpEndpointsUpdate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { McpEndpoint } from "../models/components/mcpendpoint.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateMcpEndpointRequest,
  UpdateMcpEndpointSecurity,
} from "../models/operations/updatemcpendpoint.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateMcpEndpointMutationVariables = {
  request: UpdateMcpEndpointRequest;
  security?: UpdateMcpEndpointSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateMcpEndpointMutationData = McpEndpoint;

export type UpdateMcpEndpointMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateMcpEndpoint mcpEndpoints
 *
 * @remarks
 * Update an MCP endpoint. This is a full-record replace: fields omitted from the request become null on the stored record. The id and slug fields are required, along with exactly one of mcp_server_id or meta_mcp_server_id.
 */
export function useUpdateMcpEndpointMutation(
  options?: MutationHookOptions<
    UpdateMcpEndpointMutationData,
    UpdateMcpEndpointMutationError,
    UpdateMcpEndpointMutationVariables
  >,
): UseMutationResult<
  UpdateMcpEndpointMutationData,
  UpdateMcpEndpointMutationError,
  UpdateMcpEndpointMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateMcpEndpointMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateMcpEndpoint(): MutationKey {
  return ["@gram/client", "mcpEndpoints", "update"];
}

export function buildUpdateMcpEndpointMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateMcpEndpointMutationVariables,
  ) => Promise<UpdateMcpEndpointMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateMcpEndpoint(),
    mutationFn: function updateMcpEndpointMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateMcpEndpointMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(mcpEndpointsUpdate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
